import React, { useEffect, useState } from 'react';
import { Card, Button, Alert, Navbar, Container, Nav } from 'react-bootstrap';
import { ExclamationCircle, CheckCircle } from 'react-bootstrap-icons';
import { API_URL, FILE_URL, APP_URL } from '../constants';
import axios from 'axios';
import { FormGenerator } from '../helper/FormGenerator'; // Assuming you have this helper function for form rendering
import { useSiteData } from '../hooks/provider/SiteDataProvider';
import { Link, useNavigate } from 'react-router-dom';
import { countryOptions, stateOptions } from '../utils/appUtils';
import { toast } from 'react-toastify';

const VendorCompleteProfile = () => {
    const [profileData, setProfileData] = useState({});
    const [formData, setFormData] = useState({});
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);
    const siteData = useSiteData();
    const navigate = useNavigate();
    const [extraFields, setExtraFields] = useState([]);

    const SITELOGO = siteData?.site_logo ? FILE_URL + siteData.site_logo : '/logo192.png';
    const SITENAME = siteData?.site_title ?? "Your Site Name";

    const data = localStorage.getItem('vendorProfile');
    const vendorToken = JSON.parse(data)?.temp_token || null;

    // Fetch user data on component mount
    useEffect(() => {
        if (!vendorToken) {
            setError('No profile data found in localStorage.');
            setIsLoading(false);
            navigate('/vendor/login');
            return;
        }

        fetchUserData(vendorToken);
    }, []);

    // Function to check for token expiry
    const handleTokenExpiry = (error) => {
        if (error?.response?.status === 401 || error?.response?.data?.message === 'Token expired') {
            localStorage.removeItem('vendorProfile');
            navigate('/vendor/login');
        }
    };

    // Fetch user data from API with temp_token in Authorization header
    const fetchUserData = async (token) => {
        setIsLoading(true);
        try {
            const res = await axios.get(
                `${API_URL}/vendor/get`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`, // Include temp_token as Bearer token
                    },
                }
            );

            if (res.data) {
                if (res.data.status === 1 || res.data.status === 'success') {
                    toast.success('Your profile is complete.', {
                        position: 'top-right',
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                    navigate('/vendor/login');
                    return;
                }
                
                const userData = res.data ?? {};
                setProfileData(userData);

                // Map user data to form fields, flattening nested objects
                let formDataMapped = { ...userData };
                
                // Remove nested objects that need special handling
                delete formDataMapped.documents;
                delete formDataMapped.warehouses;
                delete formDataMapped.extraDocuments;

                // Flatten documents object into formData
                if (userData.documents) {
                    Object.assign(formDataMapped, userData.documents);
                }

                // Map warehouse data to form fields
                if (userData.warehouses && userData.warehouses.length > 0) {
                    const warehouse = userData.warehouses[0];
                    formDataMapped = {
                        ...formDataMapped,
                        wh_name: warehouse.name,
                        wh_email: warehouse.email,
                        wh_phone: warehouse.phone,
                        wh_address: warehouse.address,
                        wh_address_2: warehouse.address2,
                        wh_city: warehouse.city,
                        wh_pin_code: warehouse.pinCode,
                        wh_state: warehouse.state,
                        wh_country: warehouse.country
                    };
                }

                // Load extra documents if available
                const extraDocumentsRaw = userData.extraDocuments;
                let extraDocuments = [];
                if (typeof extraDocumentsRaw === 'string') {
                    try {
                        extraDocuments = JSON.parse(extraDocumentsRaw);
                    } catch (e) {
                        extraDocuments = [];
                    }
                } else if (Array.isArray(extraDocumentsRaw)) {
                    extraDocuments = extraDocumentsRaw;
                } else {
                    extraDocuments = [];
                }
                setExtraFields(extraDocuments);

                // Add extra document URLs to formData
                extraDocuments.forEach(field => {
                    formDataMapped[field.id] = field.url;
                });

                setFormData(formDataMapped);
            } else {
                setError(res.data.message || 'Error fetching profile data.');
            }
        } catch (err) {
            handleTokenExpiry(err); // Handle token expiry or invalid token
            setError('Error fetching profile data.');
        } finally {
            setIsLoading(false);
        }
    };

    // Handle form data change
    const handleFormChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));

        // If it's an extra field, update extraFields as well
        const extraField = extraFields.find(f => f.id === field);
        if (extraField) {
            setExtraFields(extraFields.map(f =>
                f.id === field ? { ...f, url: value } : f
            ));
        }
    };

    // Handle form submission (update profile data)
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);
        setSuccess(null);

        try {
            const res = await axios.put(
                `${API_URL}/vendor/update`,
                { ...formData, extraDocuments: extraFields },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${vendorToken}`, // Send the temp_token in header
                    },
                }
            );

            if (res.data.status === 'success') {
                setSuccess('Profile updated successfully!');
                setFormData({});
                fetchUserData(vendorToken); // Re-fetch user data after successful submission
            } else {
                setError(res.data.message || 'Error submitting profile data.');
            }

            setIsSubmitting(false);
        } catch (err) {
            handleTokenExpiry(err); // Handle token expiry or invalid token
            setError('Error submitting profile data.');
            setIsSubmitting(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('vendorProfile');
        navigate('/vendor/login');
    };

    // Add extra document fields with ability to add/remove them
    const handleAddExtraField = () => {
        const fieldId = `extra_${Date.now()}`;
        setExtraFields([...extraFields, { id: fieldId, label: `Document ${extraFields.length + 1}`, url: '' }]);
    };

    const handleRemoveExtraField = (fieldId) => {
        setExtraFields(extraFields.filter(f => f.id !== fieldId));
    };

    const handleExtraFieldChange = (fieldId, fieldName, value) => {
        setExtraFields(extraFields.map(f =>
            f.id === fieldId ? { ...f, [fieldName]: value } : f
        ));
    };

    const extraFileFields = extraFields.map(f => ({
        id: f.id,
        label: f.label,
        type: 'file_1',
        class: 'col-md-6',
        required: false,
    }));

    // Personal Info Section
    const personalInfoFields = [
        { id: 'first_name', label: 'Owner First Name', type: 'text', class: 'col-md-6', required: true, readonly: true },
        { id: 'last_name', label: 'Owner Last Name', type: 'text', class: 'col-md-6', required: true, readonly: true },
        { id: 'phone', label: 'Phone Number', type: 'tel', class: 'col-md-6', required: true, readonly: true, prefix: '+91', minLength: 10, maxLength: 10, isNumeric: true },
        { id: 'email', label: 'Email', type: 'email', class: 'col-md-6', required: true, readonly: true },
        { id: 'address', label: 'Address', type: 'textarea', class: 'col-md-12', required: true, readonly: true },
        { id: 'city', label: 'City', type: 'text', class: 'col-md-6', required: true, readonly: true },
        { id: 'zip', label: 'PIN Code', type: 'text', class: 'col-md-6', required: true, readonly: true, minLength: 6, maxLength: 6, isNumeric: true },
        { id: 'state', label: 'State', type: 'select', options: stateOptions, searchable: true, class: 'col-md-6', required: true, readonly: true },
        { id: 'country', label: 'Country', type: 'select', options: countryOptions, class: 'col-md-6', required: true, readonly: true },
    ];

    // Store Info Section
    const storeInfoFields = [
        { id: 'store_name', label: 'Store Display Name', type: 'text', class: 'col-md-6', required: true },
        { id: 'store_legal_name', label: 'Store Legal Name', type: 'text', class: 'col-md-6', required: true },
        { id: 'gst', label: 'GSTIN Number', type: 'text', class: 'col-md-6', required: true },
        { id: 'fssai_number', label: 'FSSAI Number', type: 'text', class: 'col-md-6', required: true },
    ];

    // if (profileData.bankinfo_status != 'Verified') {
    const bankInfoFields = [
        { id: 'accountHolderName', label: 'Account Holder Name', type: 'text', class: 'col-md-6', required: true },
        { id: 'bankName', label: 'Bank Name', type: 'text', class: 'col-md-6', required: true },
        { id: 'accountNumber', label: 'Account Number', type: 'text', class: 'col-md-6', required: true },
        { id: 'confirmAccountNumber', label: 'Confirm Account Number', type: 'text', class: 'col-md-6', required: true },
        { id: 'ifscCode', label: 'IFSC Code', type: 'text', class: 'col-md-6', required: true },
        { id: 'branchName', label: 'Branch Name', type: 'text', class: 'col-md-6', required: true },
        { id: 'upiId', label: 'UPI ID', type: 'text', class: 'col-md-6', required: true },
    ];
    // } else {
    //     var bankInfoFields = [{type: 'heading', text: 'Bank Information Verified', textClass: 'mb-2 m-0 fs-4 mt-3 text-success', class: 'col-md-12'}];
    // }

    // Upload Section
    const uploadFields = [
        { id: 'brand_logo', label: 'Store/Brand Logo', type: 'file_1', class: 'col-md-6', required: true },
        { id: 'file_aadhar_card', label: 'Owner Aadhar Card', type: 'file_1', class: 'col-md-6', required: true },
        { id: 'file_pan_card', label: 'Owner PAN Card', type: 'file_1', class: 'col-md-6', required: true },
        { id: 'file_fssai_certificate', label: 'FSSAI Certificate', type: 'file_1', class: 'col-md-6', required: true },
        { id: 'file_bank_passbook', label: 'Bank Passbook', type: 'file_1', class: 'col-md-6', required: true },
        ...extraFileFields,
    ];

    // Warehouse Section
    const warehouseFields = [
        { id: 'wh_name', label: 'Contact Person Name', type: 'text', class: 'col-md-12', required: true },
        { id: 'wh_email', label: 'Contact Person Email', type: 'email', class: 'col-md-6', required: true },
        { id: 'wh_phone', label: 'Contact Person Phone', type: 'tel', class: 'col-md-6', required: true, maxLength: 10, minLength: 10, prefix: '+91', isNumeric: true },
        { id: 'wh_address', label: 'Address Line 1', type: 'textarea', class: 'col-md-6', required: true },
        { id: 'wh_address_2', label: 'Address Line 2', type: 'textarea', class: 'col-md-6', required: false },
        { id: 'wh_city', label: 'City', type: 'text', class: 'col-md-6', required: true },
        { id: 'wh_pin_code', label: 'Pincode', type: 'text', class: 'col-md-6', required: true, maxLength: 6, minLength: 6, isNumeric: true },
        { id: 'wh_state', label: 'State', type: 'select', options: stateOptions, searchable: true, class: 'col-md-6', required: true },
        { id: 'wh_country', label: 'Country', type: 'select', options: countryOptions, class: 'col-md-6', required: true },
        { id: 'wh_address_type', label: 'Address Type', type: 'select', options: { 'Home': 'Home', 'Warehouse': 'Warehouse', 'Other': 'Other' }, class: 'col-md-6', readonly: true },
        { id: 'gst', label: 'GSTIN', type: 'text', class: 'col-md-6', readonly: true, minLength: 15, maxLength: 15 }
    ];

    const categories = [
        { id: 'personal_info', title: 'Personal Information', fields: personalInfoFields },
        { id: 'store_info', title: 'Store Information', fields: storeInfoFields },
        { id: 'bank_info', title: 'Bank Information', fields: bankInfoFields },
        { id: 'warehouse_info', title: 'Warehouse Information', fields: warehouseFields },
        { id: 'uploads', title: 'Uploads', fields: uploadFields },
    ];

    return (
        <>
            <Navbar bg="light" expand="lg" className="shadow-sm p-0 mb-4">
                <Container>
                    <Navbar.Brand as={Link} to={APP_URL} className="d-flex align-items-center">
                        <img
                            src={SITELOGO}
                            alt={SITENAME}
                            className="logo"
                            style={{ width: '100px' }}
                        />
                    </Navbar.Brand>
                    <Nav className="ms-auto align-items-center">
                        <span className="me-3 text-muted">
                            Welcome, <span className="fw-bold text-dark">{profileData.name || 'Vendor'}</span>
                        </span>
                        <Button variant="outline-danger" size="sm" onClick={handleLogout}>
                            Logout
                        </Button>
                    </Nav>
                </Container>
            </Navbar>

            <div className="container mt-4 col-md-6 mx-auto">
                {success && <Alert variant="success" className="fade show">{success}</Alert>}
                {error && (
                    <Alert variant="danger" className="d-flex align-items-center fade show">
                        <ExclamationCircle className="me-2" />
                        {error}
                    </Alert>
                )}

                {
                    isLoading ? (
                        <div className="loading-spinner text-center">
                            <span className="spinner-border spinner-border-sm me-3" role="status" aria-hidden="true"></span>
                            Loading...
                        </div>
                    ) : (
                        <>
                            <Alert variant="danger" className="d-flex align-items-center fade show">
                                <div dangerouslySetInnerHTML={{ __html: formData.notice_board || "" }}></div>
                            </Alert>
                            <form onSubmit={handleSubmit} className="p-4 border rounded shadow-sm bg-white">
                                {/* Iterate through categories to create Card sections */}
                                {categories.map((category) => (
                                    <Card key={category.id} className="mb-4"> {/* Added mb-4 for spacing between cards */}
                                        <Card.Header className="bg-white fw-bold text-primary">
                                            {category.title}
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="row g-3"> {/* g-3 adds gutter space between columns */}
                                                {category.fields.map((field) => (
                                                    <FormGenerator
                                                        key={field.id}
                                                        fields={[field]}
                                                        fieldData={formData}
                                                        onChange={handleFormChange}
                                                        onSubmit={false}
                                                        authToken={vendorToken}
                                                        hTure={false} />
                                                ))}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                ))}

                                {/* Submit button and Login link section */}
                                <div className="col-12 mt-4">
                                    <Button type="submit" className="w-100 py-2 fw-bold" variant="primary" disabled={isSubmitting}>
                                        {isSubmitting ? 'Submitting...' : 'Submit'}
                                    </Button>
                                </div>
                            </form>
                        </>
                    )}
            </div>
        </>
    );
};

export default VendorCompleteProfile;
